import { useEffect, useRef, useState } from "react";
import MetaWindowStatusView from "./MetaWindowStatusView";
import { HelpCircle } from "lucide-react";

const ORIGIN = String(import.meta.env?.VITE_API_BASE_URL || import.meta.env?.VITE_API_URL || import.meta.env?.VITE_BACKEND_URL || "").trim().replace(/\/+$/, "");
const cache = new Map();
const CACHE_MS = 45000;

async function buscarJanela(path) {
  const token = String(localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("jwt") || sessionStorage.getItem("token") || sessionStorage.getItem("authToken") || "").trim();
  const resp = await fetch(`${ORIGIN}${path}`, {
    headers: { ...(token ? { Authorization: /^Bearer /i.test(token) ? token : `Bearer ${token}` } : {}) },
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok || body?.ok === false) {
    const motivo = String(body?.erro || body?.error || body?.message || `HTTP ${resp.status}`);
    const codigo = String(body?.code || `HTTP_${resp.status}`);
    throw new Error(`${codigo}: ${motivo}`);
  }
  return body;
}

/** Quando faltar contexto/vínculo, o status não pode sumir: isso confundia o atendente. */
function JanelaSemComprovacao({ motivo = "Sem comprovação de janela oficial para este contato." }) {
  return (
    <span
      role="status"
      data-meta-window-status="INCERTA"
      title={motivo}
      className="inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold leading-4 text-slate-600"
    >
      <HelpCircle size={12} className="shrink-0" />
      <span className="whitespace-nowrap">Janela não confirmada</span>
    </span>
  );
}

/** Um indicador compacto para o composer da conversa; nenhuma faixa extra. */
export default function MetaWindowInline({ empresaId, telefone, instancia, chamadoId, apiRequest }) {
  const requestRef = useRef(apiRequest);
  requestRef.current = apiRequest;
  const [consulta, setConsulta] = useState({ key: "", data: null, error: "" });
  const [erroExpandido, setErroExpandido] = useState(false);
  const chamadoSeguro = Number.isSafeInteger(Number(chamadoId)) && Number(chamadoId) > 0
    ? String(chamadoId) : "";
  const ativo = Boolean(empresaId && telefone && (instancia || chamadoSeguro));
  const path = ativo ? `/api/captazap/meta/templates/janela/atendimento?${new URLSearchParams({
    empresa_id: String(empresaId),
    telefone: String(telefone),
    zapi_instance_id: String(instancia || ""),
    ...(chamadoSeguro ? { chamado_id: chamadoSeguro } : {}),
  })}` : "";

  useEffect(() => {
    if (!path) return undefined;
    let valido = true;
    let emAndamento = false;
    const anterior = cache.get(path);
    setConsulta({ key: path, data: anterior?.data || null, error: "" });

    async function consultar() {
      if (emAndamento) return;
      emAndamento = true;
      try {
        const provider = requestRef.current;
        const data = typeof provider === "function" ? await provider(path) : await buscarJanela(path);
        if (!valido) return;
        cache.set(path, { data, at: Date.now() });
        if (cache.size > 100) cache.delete(cache.keys().next().value);
        setConsulta({ key: path, data, error: "" });
      } catch (err) {
        if (valido) setConsulta((prev) => ({
          key: path,
          data: prev.key === path ? prev.data : anterior?.data || null,
          error: err?.message || "Falha ao consultar a Meta",
        }));
      } finally {
        emAndamento = false;
      }
    }

    if (!anterior || Date.now() - anterior.at > CACHE_MS) consultar();
    const tick = setInterval(consultar, 60000);
    const onFocus = () => { if (!document.hidden) consultar(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      valido = false;
      clearInterval(tick);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [path]);

  if (!ativo) {
    const pendentes = [
      !empresaId && "empresa",
      !telefone && "telefone",
      !(instancia || chamadoSeguro) && "conexão ou chamado da conversa",
    ].filter(Boolean).join(", ");
    return <JanelaSemComprovacao motivo={`Não foi possível consultar a janela: falta ${pendentes}.`} />;
  }
  const atual = consulta.key === path ? consulta : {
    key: path,
    data: cache.get(path)?.data || null,
    error: "",
  };
  if (atual.data && (atual.data.exibir === false || atual.data.tipo === "SEM_META")) {
    const motivo = atual.data.tipo === "CONTEXTO_DIVERGENTE"
      ? "O telefone visível não corresponde ao chamado selecionado. Não é seguro confirmar a janela de outro contato."
      : "Esta conversa não foi associada de forma inequívoca à conexão Meta Oficial. A existência de mensagens na Z-API não comprova janela aberta na Meta.";
    return <JanelaSemComprovacao motivo={motivo} />;
  }

  // O backend pode estar correto e a requisição do navegador falhar.
  // Permite diagnosticar sem DevTools: clique no indicador para ver o HTTP/erro.
  if (atual.error && !atual.data) {
    return (
      <div className="flex max-w-full flex-col items-end gap-1">
        <button
          type="button"
          title="Clique para ver por que a consulta da janela falhou"
          aria-label="Mostrar erro da consulta da janela Meta Oficial"
          aria-expanded={erroExpandido}
          onClick={() => setErroExpandido((valor) => !valor)}
          className="max-w-full cursor-pointer rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
        >
          <MetaWindowStatusView data={null} compact error={atual.error} />
        </button>
        {erroExpandido && (
          <span role="alert" className="max-w-[310px] break-words rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-right text-[10px] font-medium leading-4 text-amber-900">
            Motivo da indisponibilidade: {String(atual.error).slice(0, 260)}
          </span>
        )}
      </div>
    );
  }
  return <MetaWindowStatusView data={atual.data} compact error={atual.error} />;
}
